import { Box, Chip, Typography } from '@mui/material';
import React from 'react';
import type { Distribution } from '../../data/types';
import { distributionTotals } from '../../domain/distributionCalc';
import { money, units as formatUnits } from '../../lib/format';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { SectionCard, StatCard, StatGrid } from '../../ui/primitives';

export default function DistributionPreview({ distribution }: { distribution: Distribution }) {
  const scope = useTenantScope();
  const totals = distributionTotals(distribution);
  return (
    <>
      <StatGrid min={170}>
        <StatCard label="Holders" value={totals.holders} />
        <StatCard label="Gross" value={money(totals.gross)} />
        <StatCard
          label="TFN withholding"
          value={money(totals.tfnWithholding)}
          tone={totals.tfnWithholding ? 'warning' : 'neutral'}
        />
        <StatCard
          label="Non-resident withholding"
          value={money(totals.nrWithholding)}
          tone={totals.nrWithholding ? 'warning' : 'neutral'}
        />
        <StatCard label="Cash to bank (ABA)" value={money(totals.cash)} tone="info" />
        <StatCard label="Reinvested (DRP)" value={money(totals.drp)} />
      </StatGrid>
      <SectionCard
        title="Per-holder entitlements"
        subtitle={`${distribution.centsPerUnit} cents per unit on units held at ${distribution.recordDate}`}
        noPadding
      >
        <DataTable
          rows={distribution.lines}
          rowKey={(row) => row.holderId}
          dense
          pageSize={15}
          defaultSort={{ key: 'gross', direction: 'desc' }}
          columns={[
            {
              key: 'holder',
              label: 'Holder',
              sortValue: (row) => scope.holderById.get(row.holderId)?.name ?? '',
              render: (row) => {
                const holder = scope.holderById.get(row.holderId);
                return (
                  <Box>
                    <Typography variant="body2">{holder?.name}</Typography>
                    {holder?.tfnStatus === 'NOT_QUOTED' && (
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label="No TFN"
                        sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }}
                      />
                    )}
                    {holder && holder.residency !== 'AU' && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`Non-resident ${holder.residency}`}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                );
              },
            },
            {
              key: 'units',
              label: 'Units',
              align: 'right',
              sortValue: (row) => row.units,
              render: (row) => formatUnits(row.units),
            },
            {
              key: 'gross',
              label: 'Gross',
              align: 'right',
              sortValue: (row) => row.gross,
              render: (row) => money(row.gross, true),
            },
            {
              key: 'tfn',
              label: 'TFN w/h',
              align: 'right',
              hideBelow: 'md',
              render: (row) => (row.tfnWithholding ? money(row.tfnWithholding, true) : '—'),
            },
            {
              key: 'nr',
              label: 'NR w/h',
              align: 'right',
              hideBelow: 'md',
              render: (row) => (row.nrWithholding ? money(row.nrWithholding, true) : '—'),
            },
            {
              key: 'net',
              label: 'Net',
              align: 'right',
              sortValue: (row) => row.net,
              render: (row) => <strong>{money(row.net, true)}</strong>,
            },
            { key: 'method', label: 'Method', render: (row) => row.method },
          ]}
        />
      </SectionCard>
    </>
  );
}
